import React, { useCallback, useEffect, useState } from 'react';
import { fetchVideos } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { VideoCard } from './VideoCard';
import { Button } from './ui/button';
import { Eye, Globe2, Lock, type LucideIcon } from 'lucide-react';
import type { Video } from '../types';

const POLL_INTERVAL_MS = 3000;
type VideoCategory = 'all' | 'public' | 'private';

const categoryOptions: Array<{
  value: VideoCategory;
  label: string;
  icon: LucideIcon;
}> = [
  { value: 'all', label: 'All', icon: Eye },
  { value: 'public', label: 'Public', icon: Globe2 },
  { value: 'private', label: 'Private', icon: Lock },
];

export const VideoList: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<VideoCategory>('all');
  const { user } = useAuth();

  const loadVideos = useCallback(async () => {
    try {
      const data = await fetchVideos();
      setVideos(data);
      setError(null);
    } catch (err) {
      setError('Unable to load videos.');
      console.error('[VideoList]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadVideos(); }, [loadVideos]);

  useEffect(() => {
    const hasProcessing = videos.some((video) => video.status === 'processing');
    if (!hasProcessing) return;
    const timer = setInterval(() => void loadVideos(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [videos, loadVideos]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-t-transparent border-foreground rounded-full animate-spin" />
          <p className="text-foreground/60 text-sm font-mono font-bold">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-destructive font-mono font-bold text-sm border-2 border-destructive px-4 py-2 inline-block bg-card shadow-neo-sm">
          {error}
        </p>
        <div>
          <Button variant="outline" onClick={() => void loadVideos()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const filteredVideos = videos.filter((video) => {
    if (selectedCategory === 'public') return video.visibility !== 'private';
    if (selectedCategory === 'private') return video.visibility === 'private';
    return true;
  });
  const selectedLabel = categoryOptions.find((option) => option.value === selectedCategory)?.label || 'All';
  const emptyText = selectedCategory === 'private'
    ? user ? 'No private videos yet.' : 'Sign in to see your private videos.'
    : selectedCategory === 'public'
      ? 'No public videos yet.'
      : 'No videos available.';

  const renderCollection = (title: string, items: Video[]) => (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3 border-b-2 border-foreground pb-2">
        <h3 className="font-black text-xl text-foreground">{title}</h3>
        <span className="font-mono text-xs font-black border-2 border-foreground bg-card px-2 py-0.5 text-foreground">
          {items.length}
        </span>
      </div>
      {items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((video) => (
            <VideoCard key={video.id} video={video} onDelete={loadVideos} />
          ))}
        </div>
      ) : (
        <div className="border-2 border-foreground bg-card p-6 text-sm font-bold text-foreground/60">
          {emptyText}
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 border-2 border-foreground bg-card p-3 shadow-neo-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-black text-foreground">Video category</p>
          <p className="text-xs font-bold text-foreground/60">Choose one local visibility filter.</p>
        </div>
        <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-3 sm:flex sm:flex-wrap" role="radiogroup" aria-label="Video category">
          {categoryOptions.map((option) => {
            const Icon = option.icon;
            const active = selectedCategory === option.value;
            return (
              <Button
                key={option.value}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                role="radio"
                aria-checked={active}
                onClick={() => setSelectedCategory(option.value)}
                className="w-full sm:w-auto"
              >
                <Icon className="w-4 h-4" />
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>

      {renderCollection(selectedLabel, filteredVideos)}
    </div>
  );
};
